import { Icon } from 'assets/images';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import {
  requestAccountDeletion,
} from 'Services/accountDeletionService';
import {
  ACCOUNT_DELETION_ERROR_MESSAGES,
  ACCOUNT_DELETION_NAVIGATION,
  ACCOUNT_DELETION_PAGE_COPY,
} from './constants';
import '../Password/forgotpassword.css';
import './accountDeletion.css';

/**
 * Success state shown after an account deletion request is submitted.
 *
 * @returns {JSX.Element} Post-submission confirmation UI.
 */
const RequestAccountDeletionSuccess = () => {
  const navigate = useNavigate();

  return (
    <div className="check-email-text">
      <p>{ACCOUNT_DELETION_PAGE_COPY.REQUEST_SUCCESS_MESSAGE}</p>
      <div className="button-wrapper">
        <button
          type="button"
          onClick={() => navigate(ACCOUNT_DELETION_NAVIGATION.LOGIN)}
          className="btn btn-primary"
        >
          {ACCOUNT_DELETION_PAGE_COPY.VERIFICATION_GO_HOME}
        </button>
      </div>
    </div>
  );
};

/**
 * Form for submitting an account deletion request by email.
 *
 * @param {Object} props - Component props.
 * @param {(values: { email: string }) => void} props.onSubmit - Submit handler.
 * @returns {JSX.Element} Account deletion request form.
 */
const RequestAccountDeletionForm = ({ onSubmit }) => {
  const navigate = useNavigate();

  return (
    <>
      <div className="reset-password-text">
        <p>{ACCOUNT_DELETION_PAGE_COPY.REQUEST_DESCRIPTION}</p>
        <p>{ACCOUNT_DELETION_PAGE_COPY.REQUEST_EXPIRY_NOTE}</p>
      </div>
      <div className="forgot-password-form-wrapper">
        <Formik
          initialValues={{ email: '' }}
          onSubmit={onSubmit}
          validationSchema={Yup.object().shape({
            email: Yup.string()
              .email(ACCOUNT_DELETION_PAGE_COPY.EMAIL_INVALID)
              .required(ACCOUNT_DELETION_PAGE_COPY.EMAIL_REQUIRED),
          })}
        >
          {({ touched, errors }) => (
            <Form>
              <div className="form-outline col-md-12 col-lg-12">
                <Field
                  type="email"
                  name="email"
                  placeholder={ACCOUNT_DELETION_PAGE_COPY.EMAIL_PLACEHOLDER}
                  className={`form-control ${
                    touched.email && errors.email ? 'is-invalid' : ''
                  }`}
                />
                <ErrorMessage
                  component="div"
                  name="email"
                  className="invalid-feedback"
                />
              </div>

              <div className="return-link-wrapper">
                <p className="link-color d-flex">
                  {ACCOUNT_DELETION_PAGE_COPY.RETURN_TO_LOGIN_PREFIX}{' '}
                  <span onClick={() => navigate(ACCOUNT_DELETION_NAVIGATION.LOGIN)}>
                    {ACCOUNT_DELETION_PAGE_COPY.RETURN_TO_LOGIN_LINK}
                  </span>
                </p>
              </div>
              <div className="button-wrapper">
                <button type="submit" className="btn btn-primary">
                  {ACCOUNT_DELETION_PAGE_COPY.REQUEST_SUBMIT_BUTTON}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

/**
 * Public page where users request account deletion without signing in.
 *
 * @returns {JSX.Element} Account deletion request page.
 */
const RequestAccountDeletion = () => {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Sends the account deletion request to the API.
   *
   * @param {{ email: string }} values - Form values from Formik.
   */
  const handleSubmit = async (values) => {
    setError(null);
    try {
      await requestAccountDeletion({ email: values.email });
      setSubmitted(true);
    } catch (submitError) {
      const apiMessage = submitError?.response?.data?.message;
      setError({
        message: apiMessage || ACCOUNT_DELETION_ERROR_MESSAGES.REQUEST_FAILED,
      });
    }
  };

  return (
    <div className="container forgot-password-wrapper account-deletion-wrapper">
      <div className="container information-wrapper">
        <div className="icon-wrapper text-start">
          <img className="image-logo-pb" src={Icon} alt="purebrand" />
        </div>
        <div className="reset-password-title text-start">
          <h3>{ACCOUNT_DELETION_PAGE_COPY.REQUEST_TITLE}</h3>
        </div>
        {submitted ? (
          <RequestAccountDeletionSuccess />
        ) : (
          <RequestAccountDeletionForm onSubmit={handleSubmit} />
        )}
        <div className="col-lg-4 mb-3 mt-3">
          {error ? (
            <Alert key="danger" variant="danger">
              {error.message}
            </Alert>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default RequestAccountDeletion;
